import React, { useEffect, useState } from "react";
import { Button, message } from "antd";
import { Content } from "antd/es/layout/layout";
import { SafetyCertificateOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { generateClient } from "aws-amplify/api";
import { getTableID } from "../Services/authServices";
import AppActionModal from "../Components/AppActionModal";
import DetailModal from "../Components/Transactions/DetailModal";
import WalletCard from "../Components/Dashboard/WalletCard";
import ConverterCard from "../Components/Dashboard/ConverterCard";
import RecentTransactions from "../Components/Dashboard/RecentTransactions";
import { getUser, walletsByUserID } from "../graphql/queries";
import { signedPost } from "../Services/awsSignedFetch";
import { getVerificationUiState } from "../Utils/verificationStatus";
import "./style.css";
import { fetchAuthSession } from "aws-amplify/auth";

const API = generateClient();
const VERIFICATION_PROMPT_TRIGGER_KEY = "dashboardVerificationPromptShouldShow";

const HISTORY_API =
  "https://0xh0tph3f1.execute-api.us-east-2.amazonaws.com/default/getTransactionHistory-staging";

const Dashboard = () => {
  const navigate = useNavigate();
  const [wallet, setWallet] = useState({
    availableBalance: 0,
    onHoldBalance: 0,
    currency: "CAD",
    id: "",
  });
  const [loading, setLoading] = useState(true);
  const [activityHistory, setActivityHistory] = useState([]);
  const [showVerificationPrompt, setShowVerificationPrompt] = useState(false);
  const [verificationUi, setVerificationUi] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 768,
  );
  const isMobile = viewportWidth < 768;
  const closeVerificationPrompt = () => {
    setShowVerificationPrompt(false);
    localStorage.setItem(VERIFICATION_PROMPT_TRIGGER_KEY, "false");
  };
  const walletCurrency = String(wallet.currency || "CAD").toUpperCase();

  const formatDateToLocal = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return dateString;
    }
  };
  useEffect(() => {
    const fetchData = async () => {
      try {
        const tokenRes = await fetchAuthSession();
        const accessToken = tokenRes.tokens.accessToken.toString();
       
        const tableID = await getTableID();
        if (!tableID) {
          console.error("Table ID is not available.");
          return;
        }

        const [walletData, userData] = await Promise.all([
          API.graphql({
            query: walletsByUserID,
            variables: { userID: tableID, limit: 1 },
          }),
          API.graphql({
            query: getUser,
            variables: { id: tableID },
          }),
        ]);
        const verificationStatus = userData?.data?.getUser || {};
        const nextVerificationUi = getVerificationUiState({
          verificationStatus: verificationStatus?.verificationStatus,
          identificationVerified: verificationStatus?.identificationVerified,
          isOrganization: verificationStatus?.isOrganization,
        });
        setVerificationUi(nextVerificationUi);

        const walletDetails = walletData?.data?.walletsByUserID?.items?.[0] || {
          availableBalance: 0,
          onHoldBalance: 0,
          currency: "CAD",
          id: "",
        };

        setWallet(walletDetails);

        try {
          const { lambdaStatus, data } = await signedPost(HISTORY_API, {
            limit: 5,
            sort: { by: "date", order: "DESC" },
          });
          if (lambdaStatus === 200) {
            setActivityHistory(data.items || []);
          }
        } catch (e) {
          console.error("[Dashboard] Failed to load recent transactions:", e);
        }
        if (!nextVerificationUi?.isVerified) {
          const shouldPromptAfterLogin =
            localStorage.getItem(VERIFICATION_PROMPT_TRIGGER_KEY) === "true";

          if (shouldPromptAfterLogin) {
            setShowVerificationPrompt(true);
          }
        }
      } catch (err) {
        console.error("Error fetching dashboard data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleContinueSend = async ({
    sendAmount,
    receiveAmount,
    receiveCurrency,
  } = {}) => {
    try {
      const tableID = await getTableID();
      if (!tableID) {
        message.error("Unable to load your account. Please sign in again.");
        return;
      }

      if (
        sendAmount === null ||
        sendAmount === undefined ||
        sendAmount === ""
      ) {
        message.error("Enter an amount to continue.");
        return;
      }

      const sendDraft = {
        sendRate: Number(sendAmount),
        getRate: Number(receiveAmount || 0),
        formattedSendRate: Number(sendAmount).toFixed(2),
        entryMethod: "wallet",
        currencyNow: walletCurrency.toLowerCase(),
        currencyNow2: receiveCurrency.toLowerCase(),
      };

      navigate("/recipients", {
        state: {
          flow: "sendMoney",
          sendDraft: {
            ...sendDraft,
            targetCurrency: receiveCurrency,
          },
        },
      });
    } catch (error) {
      console.error("Error starting send-money flow:", error);
      message.error("Unable to open send flow. Please try again.");
    }
  };

  const handleFundWallet = () => {
    if (!wallet.id) {
      message.error("Wallet is not active yet.");
      return;
    }
    navigate("/fundWallet", { state: { wallet } });
  };

  const handleSendFromWalletCard = () => {
    if (!wallet.id) {
      message.error("Wallet is not active yet.");
      return;
    }
    navigate("/recipients", {
      state: {
        flow: "sendMoney",
        sendDraft: {
          entryMethod: "walletCard",
          currencyNow: walletCurrency.toLowerCase(),
          currencyNow2: walletCurrency.toLowerCase(),
          targetCurrency: walletCurrency,
        },
      },
    });
  };

  const handleStartVerification = () => {
    localStorage.setItem(VERIFICATION_PROMPT_TRIGGER_KEY, "false");
    setShowVerificationPrompt(false);
    navigate("/kycuser");
  };

  const handleOpenTransactionDetails = (item) => {
    const raw = item.raw || item;
    setSelectedTransaction(raw);
    setDetailModalVisible(true);
  };

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <Content className="appPageContent">
      <div className="appPageShell dashboardShell">
        <div className="appSectionHeader">
          <h2 className="appSectionTitle">Dashboard</h2>
        </div>

        <div className="dashboardTopGrid">
          <WalletCard
            loading={loading}
            wallet={wallet}
            onFundWallet={handleFundWallet}
            onSendFromWalletCard={handleSendFromWalletCard}
          />

          <ConverterCard
            walletCurrency={walletCurrency}
            onContinueSend={handleContinueSend}
          />
        </div>

        <RecentTransactions
          activityHistory={activityHistory}
          onViewAll={() => navigate("/transactions")}
          onOpenTransactionDetails={handleOpenTransactionDetails}
          formatDateToLocal={formatDateToLocal}
        />
      </div>

      <AppActionModal
        open={showVerificationPrompt}
        onCancel={closeVerificationPrompt}
        className="dashboardVerifyModal"
        icon={<SafetyCertificateOutlined />}
        variant={
          verificationUi?.pillTone === "rejected"
            ? "error"
            : verificationUi?.pillTone === "pending"
              ? "info"
              : "warning"
        }
        title={verificationUi?.modalTitle || "Complete Verification"}
        description={
          verificationUi?.modalText ||
          "Complete your verification to unlock full access to wallet and transfer features."
        }
        secondaryAction={{
          label: verificationUi?.modalSecondaryLabel || "Skip for now",
          onClick: closeVerificationPrompt,
          className: "myAccountSecondaryBtn",
        }}
        primaryAction={
          verificationUi?.showModalPrimary === false
            ? null
            : {
                label:
                  verificationUi?.modalPrimaryLabel || "Start Verification",
                onClick: handleStartVerification,
              }
        }
      />

      <DetailModal
        open={detailModalVisible}
        onCancel={() => {
          setDetailModalVisible(false);
          setSelectedTransaction(null);
        }}
        selectedTransaction={selectedTransaction}
        isMobile={isMobile}
      />
    </Content>
  );
};

export default Dashboard;
